import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredMatches, advantages, articles } from '@/mocks/data';

function AnimatedNumber({ target, duration = 1500 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);

  return <span>{count.toLocaleString()}</span>;
}

export default function Home() {
  const { t } = useTranslation();

  const stats = [
    { value: 5000000, suffix: '+', label: '注册用户' },
    { value: 365, suffix: '天', label: '全年无休' },
    { value: 99.9, suffix: '%', label: '资金安全率' },
    { value: 3000, suffix: '+', label: '每日赛事' },
  ];

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '皇冠体育',
    url: 'https://huangguan-sports.com',
    description: t('seo.home_desc'),
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://huangguan-sports.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '皇冠体育',
    url: 'https://huangguan-sports.com',
    description: t('seo.home_desc'),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6666',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <SEOHead
        title={t('seo.home_title')}
        description={t('seo.home_desc')}
        keywords={t('seo.home_keywords')}
        canonicalUrl="https://huangguan-sports.com"
      />
      <script type="application/ld+json">{JSON.stringify(homeJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* ===== Hero Section ===== */}
        <header className="relative w-full h-[500px] md:h-[620px] overflow-hidden -mt-16">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=Dynamic%20sports%20stadium%20atmosphere%20with%20dramatic%20lighting%2C%20soccer%20football%20basketball%20tennis%20action%20silhouettes%2C%20professional%20sports%20photography%20style%2C%20dark%20cinematic%20mood%2C%20stadium%20lights%20glowing%2C%20epic%20sports%20energy%2C%20abstract%20geometric%20overlay%20with%20golden%20and%20green%20accents%2C%20luxurious%20dark%20gradient%20background&width=1920&height=800&seq=hero-bg-001&orientation=landscape)`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6 pt-16">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 mb-6">
                <i className="ri-vip-crown-fill text-primary-400 text-sm"></i>
                <span className="text-primary-300 text-sm font-medium">官方正版 · 安全可靠</span>
              </div>

              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
                {t('home.hero_title')}
              </h1>
              <p className="text-background-50/80 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                {t('home.hero_subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="#"
                  className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-full text-base hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-user-add-line"></i>
                  {t('home.hero_cta')}
                </a>
                <a
                  href="#"
                  className="w-full sm:w-auto px-8 py-3.5 bg-background-50/10 backdrop-blur-sm text-background-50 font-semibold rounded-full text-base border border-background-50/30 hover:bg-background-50/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-download-2-line"></i>
                  {t('home.hero_download')}
                </a>
              </div>
            </div>
          </div>
        </header>

        <main>
          {/* ===== Stats Bar ===== */}
          <section className="relative -mt-10 z-20 px-4 md:px-6">
            <div className="max-w-5xl mx-auto bg-background-50 rounded-2xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                    <AnimatedNumber target={stat.value} />
                    <span>{stat.suffix}</span>
                  </div>
                  <p className="text-foreground-500 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ===== Featured Matches ===== */}
          <section className="px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  {t('home.matches_title')}
                </h2>
                <p className="text-foreground-500 text-base">{t('home.matches_subtitle')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {featuredMatches.filter((m) => m.isHot).slice(0, 4).map((match) => (
                  <Link
                    key={match.id}
                    to={`/category/${match.category}`}
                    className="group bg-background-50 rounded-xl p-5 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <i className={`${match.leagueIcon} text-primary-500 text-sm`}></i>
                        <span className="text-xs text-foreground-500 font-medium">{match.league}</span>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">热门</span>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <div className="text-center flex-1">
                        <p className="font-bold text-foreground-900 text-sm group-hover:text-primary-500 transition-colors">{match.homeTeam}</p>
                      </div>
                      <div className="px-4 text-foreground-300 font-heading text-xl font-bold">VS</div>
                      <div className="text-center flex-1">
                        <p className="font-bold text-foreground-900 text-sm group-hover:text-primary-500 transition-colors">{match.awayTeam}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between bg-background-100 rounded-lg p-3">
                      <div className="text-center flex-1">
                        <p className="text-xs text-foreground-400">{t('home.odds_home')}</p>
                        <p className="text-primary-500 font-bold text-sm">{match.homeOdds.toFixed(2)}</p>
                      </div>
                      {match.drawOdds > 0 && (
                        <div className="text-center flex-1 border-x border-background-200">
                          <p className="text-xs text-foreground-400">{t('home.odds_draw')}</p>
                          <p className="text-foreground-600 font-bold text-sm">{match.drawOdds.toFixed(2)}</p>
                        </div>
                      )}
                      <div className="text-center flex-1">
                        <p className="text-xs text-foreground-400">{t('home.odds_away')}</p>
                        <p className="text-foreground-600 font-bold text-sm">{match.awayOdds.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 mt-3 text-xs text-foreground-400">
                      <i className="ri-time-line"></i>
                      <span>{match.time}</span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/category/football"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-primary-300 text-primary-600 font-medium rounded-full text-sm hover:bg-primary-50 transition-all whitespace-nowrap"
                >
                  查看全部赛事
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* ===== Advantages ===== */}
          <section className="bg-background-100 px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  {t('home.advantages_title')}
                </h2>
                <p className="text-foreground-500 text-base">{t('home.advantages_subtitle')}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
                {advantages.map((adv) => (
                  <div
                    key={adv.id}
                    className="bg-background-50 rounded-xl p-6 text-center border border-background-200 hover:border-primary-200 transition-all duration-300 group cursor-default"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                      <i className={`${adv.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">{adv.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{adv.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Latest Articles ===== */}
          <section className="px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
                    {t('home.news_title')}
                  </h2>
                  <p className="text-foreground-500 text-base">{t('home.news_subtitle')}</p>
                </div>
                <Link
                  to="/articles"
                  className="hidden sm:inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm whitespace-nowrap transition-colors"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {articles.slice(0, 3).map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 font-medium">
                          {article.category === 'football' ? '足球' : article.category === 'basketball' ? '篮球' : article.category === 'tennis' ? '网球' : '电竞'}
                        </span>
                        <span>{article.date}</span>
                        <span>{article.views.toLocaleString()} 阅读</span>
                      </div>
                      <h3 className="font-bold text-foreground-900 text-base mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-8 sm:hidden">
                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 text-primary-500 font-medium text-sm"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* ===== CTA ===== */}
          <section className="relative px-4 md:px-6 py-16 md:py-24 overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(https://readdy.ai/api/search-image?query=Dark%20luxurious%20sports%20lounge%20interior%20with%20warm%20golden%20ambient%20lighting%2C%20multiple%20screens%20displaying%20sports%20events%2C%20premium%20VIP%20atmosphere%2C%20elegant%20modern%20design%20with%20dark%20wood%20and%20leather%20textures%2C%20professional%20sports%20viewing%20environment&width=1920&height=600&seq=cta-bg-001&orientation=landscape)`,
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/90 via-foreground-900/80 to-foreground-900/90"></div>

            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
                {t('home.cta_title')}
              </h2>
              <p className="text-background-50/70 text-base md:text-lg mb-8 max-w-xl mx-auto">
                {t('home.cta_subtitle')}
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-background-50 font-semibold rounded-full text-base hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-star-line"></i>
                {t('home.cta_button')}
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}